import React, { useState } from 'react';
import { KeyRound, RefreshCw, Copy, Eye, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { generatePassword, setBusinessLogin, accessMessage, MIN_PASSWORD } from '../utils/businessLogin';

const inputClass = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green font-mono';

// Acceso con correo y contraseña para un negocio (sin necesidad de Gmail). Lo usa el administrador master en Administrar Clientes.
export default function BusinessLoginPanel({ business, onSaved }) {
  const [email, setEmail] = useState(business.google_calendar_id || '');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null); // { email, password } recién guardados, para copiar y compartir

  const save = async () => {
    setBusy(true);
    const result = await setBusinessLogin(business.id, { email, password });
    setBusy(false);
    if (!result.ok) { toast.error(result.error); return; }
    setSaved({ email: result.email, password });
    setPassword('');
    toast.success(result.created ? 'Acceso creado.' : 'Contraseña actualizada.');
    onSaved?.(result.email);
  };

  const copy = async (text, done) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(done);
    } catch {
      toast.error('No se pudo copiar. Selecciónalo y cópialo a mano.');
    }
  };

  return (
    <div className="space-y-2 bg-seryi-bg-dark/40 border border-seryi-bg-light rounded-xl p-3" aria-label={`Acceso de ${business.nombre_negocio}`}>
      <p className="text-[10px] text-seryi-text font-bold uppercase flex items-center gap-1.5"><KeyRound size={12} /> Acceso con correo y contraseña</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="space-y-1">
          <label htmlFor={`bl-email-${business.id}`} className="text-[10px] uppercase text-seryi-text">Correo de acceso</label>
          <input id={`bl-email-${business.id}`} type="email" className={inputClass} placeholder="cualquier correo" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`bl-pass-${business.id}`} className="text-[10px] uppercase text-seryi-text">Contraseña (mín. {MIN_PASSWORD})</label>
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <input
                id={`bl-pass-${business.id}`} type={visible ? 'text' : 'password'} className={`${inputClass} pr-8`} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'} className="absolute right-2 top-2 text-seryi-text hover:text-seryi-heading">
                {visible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <button type="button" onClick={() => { setPassword(generatePassword()); setVisible(true); }} aria-label="Generar contraseña" title="Generar una contraseña segura" className="px-2.5 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl text-seryi-text hover:text-seryi-green">
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button" onClick={save} disabled={busy || !email.trim() || !password}
          className="px-3 py-1.5 bg-seryi-green text-seryi-bg-dark rounded-lg text-xs font-extrabold disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} {business.google_calendar_id ? 'Guardar contraseña' : 'Crear acceso'}
        </button>
        <span className="text-[10px] text-seryi-text/80">Con este correo y contraseña el negocio entra en la pantalla de inicio de sesión.</span>
      </div>
      {saved && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 text-[11px] text-emerald-200 space-y-1.5">
          <p>Listo. Compártela ahora: por seguridad no se vuelve a mostrar.</p>
          <p className="font-mono">{saved.email} · {saved.password}</p>
          <button
            type="button" onClick={() => copy(accessMessage({ businessName: business.nombre_negocio, ...saved }), 'Mensaje copiado: pégalo en WhatsApp.')}
            className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg font-bold flex items-center gap-1.5"
          >
            <Copy size={12} /> Copiar mensaje para WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}
